<template>
  <el-button
    type="success"
    size="small"
    :loading="fetchConnectionInProgress"
    :disabled="disabled"
    class="w-24"
    @click="openConnectDialog()"
  >
    <span v-if="isConnected">Open chat</span>
    <span v-else-if="fetchConnectionInProgress">Loading</span>
    <span v-else><slot name="notConnectedLabel">Message</slot></span>
    <Modal v-model="isOpen" :title="title">
      <template v-if="isOpen">
        <div class="pb-8">
          <TextareaField v-model="form.message" label="Message" :rows="3" />
        </div>
        <Button
          type="primary"
          :loading="postInProgress"
          :disabled="!isDisplayNameSet"
          @click="confirmConnect()"
        >
          Send
        </Button>
        <Button @click="closeModal()"> Cancel </Button>

        <p v-if="!isDisplayNameSet" class="my-2 text-red-400">
          You have to set your name before connecting with a startup. Please use
          "Change" link on the right.
        </p>

        <DisplayNameInfo @on-modal-close="onDisplayNameModalClose" />
      </template>
    </Modal>
  </el-button>
</template>

<script>
import { computed, ref } from '@vue/composition-api'
import { getEmptyStartup } from '@/store/startup-model'
import Modal from 'components/common/Modal'
import Button from 'components/common/Button'
import useState from 'states/generics/useState'
import useUser from 'states/useUser'
import useConnectWithStartup from './useConnectWithStartup'
import useConnectInvestorWithStartup from './useConnectInvestorWithStartup'
import TextareaField from 'components/forms/TextareaField.vue'
import DisplayNameInfo from 'pages/chat/DisplayNameInfo'
import $message from 'services/messages'
import router from 'routes'
import { getNewAppUrl } from '../../../utils/urls'

export default {
  components: {
    Button,
    Modal,
    TextareaField,
    DisplayNameInfo,
  },
  props: {
    startup: { type: Object, default: () => getEmptyStartup() },
    orgId: { type: String },
    disabled: { type: Boolean, default: false },
  },
  setup(props, { root }) {
    const { user } = useUser()

    const form = ref({
      message: `Hi! Your project seems interesting to me. I would like to find out more about it. Would you like to schedule a call?`,
    })

    const orgMode = user.value.isOrganizer && props.startup.has_org_access
    const connectWithStartupHook = orgMode
      ? useConnectWithStartup
      : useConnectInvestorWithStartup

    const {
      isConnected,
      threadId,
      fetchConnectionInProgress,
      connect,
      postInProgress,
    } = connectWithStartupHook({
      orgId: props.orgId,
      startupId: props.startup.id,
      is_obfuscated: props.startup.is_obfuscated,
    })
    const confirmConnect = async () => {
      const { message } = form.value
      await connect({ message })
      closeModal()
      $message.success(
        `You've successfully connected with ${props.startup.name}`,
      )
    }

    const title = computed(() => {
      return `Connect with startup: ${props.startup.name}`
    })

    const [isOpen, setIsOpen] = useState(false)
    const openConnectDialog = async () => {
      if (isConnected.value) {
        window.location = getNewAppUrl(
          router.resolve({
            name: 'chatThreadMessages',
            params: { threadId: threadId.value },
          }).href,
        )
        return
      }
      setIsOpen(true)
    }
    const closeModal = () => {
      setIsOpen(false)
    }

    const isDisplayNameSet = computed(() => !!user.value.display_name)

    const onDisplayNameModalClose = () => {
      if (!isDisplayNameSet.value) {
        closeModal()
      }
    }

    return {
      isDisplayNameSet,
      onDisplayNameModalClose,

      isConnected,
      fetchConnectionInProgress,
      confirmConnect,

      form,

      title,
      isOpen,
      openConnectDialog,
      closeModal,
      postInProgress,
    }
  },
}
</script>
